import { loadMessages as loadBenchmarkingMessages } from '@atlassian/twg-benchmarking-experience/i18n-loader';
import { loadMessages as loadCommonMessages } from '@atlassian/twg-microsite-common/i18n-loader';

/**
 * Single source of truth for the locales the microsite supports. Each entry describes:
 *   - `code`        BCP-47 tag used by react-intl (`pt-BR`, not `pt_BR`)
 *   - `nativeName`  How the locale labels itself in the dropdown
 *   - `loadMessages` Loads, in parallel, the messages of `@atlassian/twg-benchmarking-experience`
 *                    and `@atlassian/twg-microsite-common` (through their `./i18n-loader`) and
 *                    this package's Traduki bundle, merged into one map
 *
 * This package's dynamic-import paths use the underscore form because
 * that's how Traduki names the files (see ./src/i18n/*.ts). Some supported
 * locale tags intentionally map to language-only Traduki bundles.
 */

export type LocaleCode =
	| 'en'
	| 'ja-JP'
	| 'de-DE'
	| 'fr-FR'
	| 'es'
	| 'pt-BR'
	| 'ko-KR'
	| 'zh-CN'
	| 'it-IT'
	| 'nl-NL'
	| 'pl-PL'
	| 'ru-RU';

export type LocaleMessages = Record<string, string>;

export type LocaleDescriptor = {
	code: LocaleCode;
	nativeName: string;
	loadMessages: () => Promise<LocaleMessages>;
};

const importDefault = async <T>(p: Promise<{ default: T }>): Promise<T> => (await p).default;

/**
 * Later bundles win on duplicate message IDs, so callers pass this package's bundle last.
 * A bundle resolving to `undefined` (a locale its package does not ship) adds nothing.
 * Rejects only when every bundle fails, so one missing bundle never drops the others.
 */
const mergeMessageBundles = async (
	bundles: readonly Promise<LocaleMessages | undefined>[],
): Promise<LocaleMessages> => {
	const results = await Promise.allSettled(bundles);
	let merged: LocaleMessages = {};
	let loadedCount = 0;

	results.forEach((result) => {
		if (result.status === 'fulfilled') {
			merged = { ...merged, ...result.value };
			loadedCount += 1;
			return;
		}
		// eslint-disable-next-line no-console
		console.error('[LocaleProvider] Failed to load a messages bundle', result.reason);
	});

	if (loadedCount === 0) {
		throw new Error('[LocaleProvider] Failed to load every messages bundle');
	}

	return merged;
};

const loadLocaleMessages = (
	code: LocaleCode,
	loadOwnBundle: () => Promise<LocaleMessages>,
): Promise<LocaleMessages> =>
	mergeMessageBundles([loadBenchmarkingMessages(code), loadCommonMessages(code), loadOwnBundle()]);

export const DEFAULT_LOCALE: LocaleCode = 'en';

export const SUPPORTED_LOCALES: readonly LocaleDescriptor[] = [
	{
		code: DEFAULT_LOCALE,
		nativeName: 'English',
		loadMessages: () =>
			loadLocaleMessages(DEFAULT_LOCALE, () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-en" */ '../../i18n/en'),
				),
			),
	},
	{
		code: 'ja-JP',
		nativeName: '日本語',
		loadMessages: () =>
			loadLocaleMessages('ja-JP', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-ja" */ '../../i18n/ja'),
				),
			),
	},
	{
		code: 'de-DE',
		nativeName: 'Deutsch',
		loadMessages: () =>
			loadLocaleMessages('de-DE', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-de" */ '../../i18n/de'),
				),
			),
	},
	{
		code: 'fr-FR',
		nativeName: 'Français',
		loadMessages: () =>
			loadLocaleMessages('fr-FR', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-fr" */ '../../i18n/fr'),
				),
			),
	},
	{
		code: 'es',
		nativeName: 'Español',
		loadMessages: () =>
			loadLocaleMessages('es', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-es" */ '../../i18n/es'),
				),
			),
	},
	{
		code: 'pt-BR',
		nativeName: 'Português (Brasil)',
		loadMessages: () =>
			loadLocaleMessages('pt-BR', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-pt_BR" */ '../../i18n/pt_BR'),
				),
			),
	},
	{
		code: 'ko-KR',
		nativeName: '한국어',
		loadMessages: () =>
			loadLocaleMessages('ko-KR', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-ko" */ '../../i18n/ko'),
				),
			),
	},
	{
		code: 'zh-CN',
		nativeName: '简体中文',
		loadMessages: () =>
			loadLocaleMessages('zh-CN', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-zh" */ '../../i18n/zh'),
				),
			),
	},
	{
		code: 'it-IT',
		nativeName: 'Italiano',
		loadMessages: () =>
			loadLocaleMessages('it-IT', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-it" */ '../../i18n/it'),
				),
			),
	},
	{
		code: 'nl-NL',
		nativeName: 'Nederlands',
		loadMessages: () =>
			loadLocaleMessages('nl-NL', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-nl" */ '../../i18n/nl'),
				),
			),
	},
	{
		code: 'pl-PL',
		nativeName: 'Polski',
		loadMessages: () =>
			loadLocaleMessages('pl-PL', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-pl" */ '../../i18n/pl'),
				),
			),
	},
	{
		code: 'ru-RU',
		nativeName: 'Русский',
		loadMessages: () =>
			loadLocaleMessages('ru-RU', () =>
				importDefault(
					import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-ru" */ '../../i18n/ru'),
				),
			),
	},
] as const;

/** Locales offered in the footer dropdown (excludes default fallback English). */
export const SELECTABLE_LOCALES: readonly LocaleDescriptor[] = SUPPORTED_LOCALES;

export const isSupportedLocale = (value: string | null | undefined): value is LocaleCode =>
	value !== null && value !== undefined && SUPPORTED_LOCALES.some((l) => l.code === value);

export const getDescriptor = (code: LocaleCode): LocaleDescriptor =>
	// Safe non-null assert: type guard ensures `code` is in the catalog.
	SUPPORTED_LOCALES.find((l) => l.code === code) as LocaleDescriptor;
